// Recover-pass modal — re-mints a wallet save link for an existing card and
// shows it as a QR (scan in person) plus a copyable link (send over chat).
// Shared by the owner Customers page and the staff app's customer search
// (staff only for programs with the owner's `staff_can_recover_pass` checkbox
// on — the server enforces that on POST /api/cards/:cardId/save-tokens).

import { useEffect, useRef, useState } from 'react'
import { QRCodeSVG } from 'qrcode.react'
import { toast } from 'sonner'

import { useRecoverPass } from '../../owner/hooks/useCustomers.js'
import { OwnerApiError } from '../../owner/ownerApi.js'

export function RecoverPassModal({
  cardId,
  customerName,
  onClose,
}: {
  cardId: string
  customerName: string | null | undefined
  onClose: () => void
}) {
  const recover = useRecoverPass()
  const name = customerName?.trim() || 'this customer'

  // The save link expires in 10 min, so it's generated on demand; "Generate
  // again" re-mints if it lapses.
  const [result, setResult] = useState<{ save_url: string } | null>(null)

  function mint() {
    recover.mutate(cardId, {
      onSuccess: (res) => setResult({ save_url: res.save_url }),
      onError: (err) => {
        const msg =
          err instanceof OwnerApiError
            ? err.userMessage
            : 'Could not generate a recovery link'
        toast.error(msg)
      },
    })
  }

  // Mint exactly once when the modal opens. The ref guard stops React 18
  // StrictMode's double-invoke (or any re-render) from minting twice.
  const mintedRef = useRef(false)
  useEffect(() => {
    if (mintedRef.current) return
    mintedRef.current = true
    mint()
    // mint is stable enough for a one-shot; deps intentionally empty.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div
      className="owner-modal-backdrop"
      role="presentation"
      onClick={onClose}
    >
      <div
        className="owner-modal"
        role="dialog"
        aria-modal="true"
        aria-label="Recover pass"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="owner-modal-head">
          <h3>Recover pass</h3>
          <p className="owner-card-sub" style={{ margin: 0 }}>
            Have {name} scan this with their phone to re-add their pass to
            their wallet.
          </p>
        </div>

        {recover.isPending && (
          <p className="owner-loading" style={{ marginTop: 16 }}>
            Generating link…
          </p>
        )}

        {result && (
          <>
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                padding: '16px 0',
              }}
            >
              <div
                style={{
                  background: '#fff',
                  padding: 14,
                  borderRadius: 12,
                }}
              >
                <QRCodeSVG
                  value={result.save_url}
                  size={220}
                  level="M"
                  includeMargin
                />
              </div>
            </div>
            {/* The link, visible and copyable — for sending over Viber /
                Messenger when the customer isn't physically present to
                scan the QR. */}
            <div className="owner-field" style={{ marginTop: 4 }}>
              <label className="owner-label" htmlFor="recover-link">
                Recovery link
              </label>
              <input
                id="recover-link"
                className="owner-input"
                readOnly
                value={result.save_url}
                onFocus={(e) => e.currentTarget.select()}
                style={{ fontSize: '0.82rem' }}
              />
            </div>
            <p className="owner-card-sub" style={{ textAlign: 'center' }}>
              This link expires in 10 minutes. If it lapses before they use
              it, generate a new one.
            </p>
            <div className="owner-form-actions" style={{ marginTop: 8 }}>
              <button
                type="button"
                className="owner-btn"
                onClick={() => {
                  if (navigator.clipboard?.writeText) {
                    void navigator.clipboard
                      .writeText(result.save_url)
                      .then(() => toast.success('Link copied'))
                      .catch(() => toast.error('Copy failed'))
                  }
                }}
              >
                Copy link
              </button>
              <button
                type="button"
                className="owner-btn"
                onClick={mint}
                disabled={recover.isPending}
              >
                Generate again
              </button>
            </div>
          </>
        )}

        <div className="owner-form-actions" style={{ marginTop: 12 }}>
          <button
            type="button"
            className="owner-btn owner-btn-primary owner-btn-block"
            onClick={onClose}
          >
            Done
          </button>
        </div>
      </div>
    </div>
  )
}
